import { useMemo, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { useSession } from '../../lib/auth/session.js';
import { useRingGroups, useSaveRingGroup, useDeleteTms } from '../../lib/api/hooks-tms.js';
import { useExtensions } from '../../lib/api/hooks.js';
import { useAdminGroups } from '../../lib/api/hooks-extension.js';
import { TableSkeleton, Empty, ErrorBanner, Segmented, SubHead, SectionHead, EditorHead } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { RowAction, openRowProps } from '../../components/ui/RowAction.js';
import { useOpenItem } from '../../lib/openitem.js';
import { useRoutingData, referrers, inUseLabel } from './usedby.js';
import { UnsavedGuard } from '../../components/ui/UnsavedGuard.js';
import { DragPicker, type PickItem } from '../../components/ui/DragPicker.js';
import { DestinationPicker, DestinationLabel } from '../../components/telephony/DestinationPicker.js';
import type { RingGroup, Destination } from '@arcnet/contracts';
import type { PanelMode } from './flows/panel.js';
import { StepHistory } from './StepHistory.js';
import { confirmAction } from '../../components/ui/Confirm.js';

type Strategy = RingGroup['strategy'];

/**
 * Hunt lists: several people rung for one call.
 *
 * Three strategies and only three, because they are what a hunt list can do on
 * its own: everybody at once, one after another in the order listed, or one
 * after another in an order drawn for each call. Round robin and longest idle
 * need to remember who took the last call, which is a queue's job; they were
 * offered here once and rang everybody at once (docs/26 D6).
 */
const STRATEGIES: Array<[Strategy, string]> = [
  ['simultaneous', 'All at once'], ['sequential', 'In order'], ['random', 'Random order'],
];
const STRATEGY_LABEL = new Map<string, string>(STRATEGIES);

const STRATEGY_HINT: Record<Strategy, string> = {
  simultaneous: 'Everybody rings together; the first to answer takes the call.',
  sequential: 'One at a time, from the top of the list.',
  random: 'One at a time, in a different order on every call.',
};

/** The cost-centre filter's value for people in none; it cannot collide with an id. */
const UNASSIGNED = '__unassigned__';

export function RingGroupsTab({ tenantId }: { tenantId: string }) {
  // Creating, editing and deleting need ringgroup:write, as the API requires (permission sweep, 19 September 2026).
  const mayWrite = useSession((s) => s.can)('ringgroup:write');
  const groups = useRingGroups(tenantId);
  const del = useDeleteTms(tenantId, 'ring-groups');
  // The open one is in the address, by its name (owner, 21 October 2026: short URLs on every routing page).
  const opened = useOpenItem(groups.data?.data, (x) => x.name);
  const editing = opened.item;
  /** Who sends calls to each one, for the Reached from column and the bin (UX review, 21 October 2026). */
  const routing = useRoutingData(tenantId);
  // Bumped by a restore from History, so the editor opens again on what was written.
  const [rev, setRev] = useState(0);

  // An editor takes the list's place, so there is one thing on the page (owner, 29 September 2026).
  if (opened.creating || editing) {
    return (
      <>
      <EditorHead back="Hunt lists" onBack={opened.close}
        title={editing ? editing.name : 'New hunt list'}
        action={editing && (
          <StepHistory tenantId={tenantId} kind="ring_group" id={editing.id} name={editing.name} onRestored={() => setRev((r) => r + 1)} />
        )} />
      <UnsavedGuard key={`${editing?.id ?? 'new'}:${rev}`} readOnly={!mayWrite}>
        <RingGroupEditor tenantId={tenantId} existing={editing} onDone={opened.close} />
      </UnsavedGuard>
      </>
    );
  }

  return (
    <>
      <SectionHead title="Hunt lists"
        sub="Ring several people for one call, in steps: together, in order or at random, with a recording between steps if you like."
        action={mayWrite && (
        <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
          onClick={opened.openNew}>
          <Icon name="plus" size={13} />
          New hunt list
        </button>
        )} />

      {groups.error && <ErrorBanner error={groups.error} />}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Name</th><th>Dial</th><th>Steps</th><th>Members</th>
                <th>If nobody answers</th><th>Reached from</th><th />
              </tr>
            </thead>
            {groups.data === undefined ? <TableSkeleton query={groups} cols={7} /> : (
              <tbody>
                {groups.data?.data.map((g) => (
                  <tr key={g.id} {...openRowProps(() => opened.open(g))}>
                    <td style={{ fontWeight: 550 }}>{g.name}</td>
                    <td className="arc-mono">{g.extension ?? <span className="arc-subtle">—</span>}</td>
                    <td>
                      {g.steps.length > 1
                        ? `${g.steps.length} steps`
                        : `${STRATEGY_LABEL.get(g.strategy) ?? g.strategy}, ${g.ring_time_sec}s${g.strategy === 'simultaneous' ? '' : ' each'}`}
                    </td>
                    <td>
                      <span className="arc-muted">{g.members.length}</span>
                      {g.members.length > 0 && (
                        <span className="arc-subtle">
                          {' · '}{g.members.slice(0, 3).map((m) => m.extension_number ?? m.external_number).join(', ')}
                          {g.members.length > 3 ? ' …' : ''}
                        </span>
                      )}
                    </td>
                    <td><DestinationLabel tenantId={tenantId} destination={g.timeout_destination} /></td>
                    <td className="arc-muted" style={{ fontSize: 12.5 }}>{referrers('ring_group', g.id, routing).length === 0 ? <span className="arc-subtle">nothing yet</span> : referrers('ring_group', g.id, routing).join(', ')}</td>
                    <td className="arc-td-right">
                      <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
                        {mayWrite && (
                        <RowAction kind="edit" label={`Edit ${g.name}`}
                          onClick={() => opened.open(g)} />
                        )}
                        {mayWrite && (
                        <RowAction kind="delete"
                          disabled={referrers('ring_group', g.id, routing).length > 0}
                          label={referrers('ring_group', g.id, routing).length > 0 ? inUseLabel(g.name, referrers('ring_group', g.id, routing)) : `Delete ${g.name}`}
                          onClick={() => {
                            void confirmAction({ title: `Delete hunt list '${g.name}'?`, action: 'Delete', danger: true })
                              .then((ok) => { if (ok) del.mutate(g.id); });
                          }} />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {groups.data !== undefined && groups.data?.data.length === 0 && (
          <Empty icon="users" title="No hunt lists"
            hint="A hunt list rings a group of people for one incoming call." />
        )}
      </div>
    </>
  );
}

/** One person or number on a step: an extension of the account, or an outside number (0144). */
type StepMember = { kind: 'ext'; id: string } | { kind: 'out'; number: string };
interface StepDraft { strategy: Strategy; ring: string; prompt: string; members: StepMember[] }

const MAX_STEPS = 10;

/** What someone types for an outside number, as +E.164: 082 555 0142 and 27825550142 both work. */
function outsideE164(raw: string): string | null {
  const d = raw.replace(/[^\d+]/g, '');
  const digits = d.replace(/\D/g, '');
  if (d.startsWith('+')) return /^\+[1-9]\d{6,14}$/.test(`+${digits}`) ? `+${digits}` : null;
  if (/^0\d{9}$/.test(digits)) return `+27${digits.slice(1)}`;
  if (/^27\d{9}$/.test(digits)) return `+${digits}`;
  return null;
}

/** The steps a saved list opens with: its own, or one step made from the list (saved before 0143). */
function draftsOf(g: RingGroup | null): StepDraft[] {
  if (!g) return [{ strategy: 'simultaneous', ring: '20', prompt: '', members: [] }];
  const steps = g.steps.length > 0 ? g.steps : [{ seq: 1, strategy: g.strategy, ring_sec: g.ring_time_sec, prompt_media_id: null }];
  return steps.map((s) => ({
    strategy: (['simultaneous', 'sequential', 'random'].includes(s.strategy) ? s.strategy : 'sequential') as Strategy,
    ring: String(s.ring_sec),
    prompt: s.prompt_media_id ?? '',
    members: g.members
      .filter((m) => ((m as { step?: number }).step ?? 1) === s.seq)
      .flatMap((m): StepMember[] => m.extension_id ? [{ kind: 'ext', id: m.extension_id }]
        : m.external_number ? [{ kind: 'out', number: m.external_number }] : []),
  }));
}

export function RingGroupEditor({ tenantId, existing, onDone, panel }: {
  tenantId: string; existing: RingGroup | null; onDone: () => void;
  /** In a call flow's side panel: apply into the draft instead of saving. */
  panel?: PanelMode;
}) {
  const save = useSaveRingGroup(tenantId);
  // Everybody, not the first page: a list of five hundred is picked from the whole account.
  const extensions = useExtensions(tenantId, { limit: 1000 });
  const costCentres = useAdminGroups(tenantId);
  // Recordings to play before a step (0143): the account's prompts, as the IVR and queue use.
  const media = useQuery({
    queryKey: ['media', tenantId],
    queryFn: () => api.get<{ data: Array<{ id: string; kind: string; name: string }> }>(`/v1/tenants/${tenantId}/media`),
    enabled: !!tenantId,
  });
  const recordings = (media.data?.data ?? []).filter((m) => m.kind === 'prompt' || m.kind === 'announcement' || m.kind === 'greeting');

  const [name, setName] = useState(existing?.name ?? '');
  const [code, setCode] = useState(existing?.extension ?? '');
  const [timeout_, setTimeout_] = useState<Destination | null>(existing?.timeout_destination ?? null);
  const [steps, setSteps] = useState<StepDraft[]>(() => draftsOf(existing));
  const [active, setActive] = useState(0);
  const [outsideSeat, setOutsideSeat] = useState(existing?.outside_extension_id ?? '');

  const [centre, setCentre] = useState('');
  const [number, setNumber] = useState('');

  const everyone = useMemo(() => extensions.data?.data ?? [], [extensions.data]);
  const byId = useMemo(() => new Map(everyone.map((e) => [e.id, e])), [everyone]);
  const step = steps[active] ?? steps[0]!;
  const hasOutside = steps.some((s) => s.members.some((m) => m.kind === 'out'));
  const typed = outsideE164(number);

  const edit = (fn: (s: StepDraft) => StepDraft) => setSteps((all) => all.map((s, i) => (i === active ? fn(s) : s)));
  const addOutside = () => {
    if (!typed) return;
    edit((s) => (s.members.some((m) => m.kind === 'out' && m.number === typed) ? s
      : { ...s, members: [...s.members, { kind: 'out', number: typed }] }));
    setNumber('');
  };
  const moveStep = (i: number, by: -1 | 1) => setSteps((all) => {
    const j = i + by;
    if (j < 0 || j >= all.length) return all;
    const next = [...all];
    [next[i], next[j]] = [next[j]!, next[i]!];
    setActive(j);
    return next;
  });
  // The picker works in ids: an extension's own, or "out:" and the number for an outside one.
  const idOf = (m: StepMember) => (m.kind === 'ext' ? m.id : `out:${m.number}`);
  const memberOf = (id: string): StepMember => (id.startsWith('out:') ? { kind: 'out', number: id.slice(4) } : { kind: 'ext', id });
  const extItems: PickItem[] = everyone.map((e) => ({
    id: e.id, code: e.number, label: e.display_name, detail: e.admin_group_name ?? undefined,
    search: `${e.number} ${e.display_name} ${e.admin_group_name ?? ''}`,
  }));
  const inCentre = (it: PickItem) => {
    if (centre === '') return true;
    const e = byId.get(it.id);
    return centre === UNASSIGNED ? !e?.admin_group_id : e?.admin_group_id === centre;
  };
  const memberLabel = (m: StepMember): string => (m.kind === 'out' ? m.number
    : `${byId.get(m.id)?.number ?? '?'} ${byId.get(m.id)?.display_name ?? ''}`.trim());

  const empty = steps.findIndex((s) => s.members.length === 0);
  const why = empty >= 0 ? `Step ${empty + 1} has nobody to ring.`
    : hasOutside && !outsideSeat ? 'Choose which extension outside numbers are charged to.' : null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (why) return;
    const body = {
      name, extension: code || null,
      // Step 1, for anything that reads the list the older way.
      strategy: steps[0]!.strategy, ring_time_sec: Number(steps[0]!.ring),
      skip_busy: true,
      timeout_destination: timeout_,
      members: [],
      steps: steps.map((s) => ({
        strategy: s.strategy, ring_sec: Number(s.ring), prompt_media_id: s.prompt || null,
        members: s.members.map((m) => (m.kind === 'ext' ? { extension_id: m.id } : { external_number: m.number })),
      })),
      outside_extension_id: hasOutside ? outsideSeat || null : null,
    };
    if (panel) { panel.onApply(body); return; }
    save.mutate({ ...(existing ? { id: existing.id } : {}), ...body }, { onSuccess: onDone });
  };

  /**
   * What the caller hears before a step: between steps, because that is when it plays (after the
   * one before rang out). Before step 1 it is the first thing the caller hears.
   */
  const link = (i: number) => {
    const s = steps[i]!;
    return (
      <div className="arc-flow-link">
        <span className="arc-flow-rail" aria-hidden />
        <div className="arc-flow-body">
          <label htmlFor={`rg-prompt-${i}`} className="arc-flow-say">
            {i === 0 ? 'The caller first hears' : `Nobody answered step ${i}, so play`}
          </label>
          <select id={`rg-prompt-${i}`} className="arc-select" style={{ width: 'auto', minWidth: 200 }} value={s.prompt}
            onChange={(e) => setSteps((all) => all.map((x, k) => (k === i ? { ...x, prompt: e.target.value } : x)))}>
            <option value="">{i === 0 ? 'Ringing' : 'Nothing, keep ringing'}</option>
            {recordings.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          {i > 0 && <span className="arc-flow-say">then ring step {i + 1}</span>}
          {s.prompt && (
            <p className="arc-hint" style={{ flexBasis: '100%', margin: 0 }}>
              Playing a recording answers the call, so the caller hears ringing from us after it.
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <form id={panel?.formId} onSubmit={submit}>
      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'minmax(170px, 1.3fr) minmax(110px, 0.5fr)', maxWidth: 520 }}>
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="rg-name">Name</label>
            <input id="rg-name" className="arc-input" required value={name}
              onChange={(e) => setName(e.target.value)} placeholder="Sales team" />
          </div>
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="rg-code">Dial code</label>
            <input id="rg-code" className="arc-input arc-mono" inputMode="numeric" maxLength={6}
              value={code} placeholder="Optional"
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
          </div>
        </div>
        <p className="arc-hint" style={{ marginBottom: 0 }}>
          The dial code is optional: colleagues dial it to reach the list. It shares numbers with
          extensions, queues, IVR menus and conference rooms, and it is not a feature code.
        </p>
      </div>

      {/* The call's path, top to bottom (owner, 29 September 2026: "the layout is not logical"):
          what the caller hears, each step, what plays between them, and where it ends. */}
      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <SubHead sub="A call rings step 1, then each next step in turn, until somebody answers. Click a step to change it.">
          What happens to a call
        </SubHead>
        <div className="arc-steps">
          {steps.map((s, i) => {
            const open = i === active;
            return (
              <div key={i}>
                {link(i)}
                <div className={`arc-step${open ? ' is-open' : ''}`}>
                  <div className="arc-step-head">
                    <span className="arc-step-n">{i + 1}</span>
                    <button type="button" className="arc-step-sum" aria-expanded={open} onClick={() => setActive(i)}>
                      <b>Step {i + 1}: {STRATEGY_LABEL.get(s.strategy)?.toLowerCase()}, {s.ring || '?'}s{s.strategy === 'simultaneous' ? '' : ' each'}</b>
                      <span className="arc-truncate">
                        {s.members.length === 0 ? 'nobody yet' : s.members.map(memberLabel).join(', ')}
                      </span>
                    </button>
                    <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon" aria-label={`Move step ${i + 1} up`}
                      disabled={i === 0} onClick={() => moveStep(i, -1)}><Icon name="chevronUp" size={12} /></button>
                    <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon" aria-label={`Move step ${i + 1} down`}
                      disabled={i === steps.length - 1} onClick={() => moveStep(i, 1)}><Icon name="chevronDown" size={12} /></button>
                    {steps.length > 1 && (
                      <RowAction kind="delete" label={`Remove step ${i + 1}`} onClick={() => {
                        setSteps((all) => all.filter((_, k) => k !== i));
                        setActive((a) => Math.max(0, a >= i ? a - 1 : a));
                      }} />
                    )}
                  </div>

                  {open && (
                    <div className="arc-step-body">
                      <div className="arc-row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 6 }}>
                        <div className="arc-field" style={{ margin: 0 }}>
                          <span className="arc-label">Ring</span>
                          <Segmented label={`How step ${i + 1} rings`} value={s.strategy} options={STRATEGIES}
                            onChange={(v) => edit((x) => ({ ...x, strategy: v }))} />
                        </div>
                        <div className="arc-field" style={{ margin: 0, maxWidth: 130 }}>
                          <label className="arc-label" htmlFor={`rg-time-${i}`}>
                            {s.strategy === 'simultaneous' ? 'For (s)' : 'Each for (s)'}
                          </label>
                          <input id={`rg-time-${i}`} className="arc-input arc-num" type="number" min={5} max={300}
                            value={s.ring} onChange={(e) => edit((x) => ({ ...x, ring: e.target.value }))} />
                        </div>
                      </div>
                      <p className="arc-hint" style={{ marginTop: 0, marginBottom: 10 }}>{STRATEGY_HINT[s.strategy]}</p>

                      {extensions.data === undefined ? <TableSkeleton query={extensions} rows={4} cols={1} /> : (
                        <DragPicker
                          chosenTitle={`Step ${i + 1}`} availableTitle="Extensions" empty="Nobody yet."
                          chosenNote={s.strategy === 'sequential' ? 'ringing from the top' : s.strategy === 'random' ? 'order drawn per call' : 'ringing together'}
                          ordered={s.strategy === 'sequential'}
                          items={[...extItems, ...s.members.flatMap((m): PickItem[] => (m.kind === 'out'
                            ? [{ id: idOf(m), code: m.number, label: <span className="arc-subtle">outside number</span>, search: m.number }] : []))]}
                          chosen={s.members.map(idOf)}
                          onChange={(ids) => edit((x) => ({ ...x, members: ids.map(memberOf) }))}
                          availableFilter={inCentre}
                          availableTools={(
                            <select className="arc-select" style={{ flex: '0 1 150px', width: 'auto' }} aria-label="Department"
                              value={centre} onChange={(e) => setCentre(e.target.value)}>
                              <option value="">All departments</option>
                              {(costCentres.data?.data ?? []).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                              <option value={UNASSIGNED}>No department</option>
                            </select>
                          )}
                          chosenFoot={(
                            <>
                              <input className="arc-input arc-mono" style={{ flex: '1 1 150px' }} inputMode="tel"
                                aria-label="Outside number" placeholder="Outside number, e.g. 082 555 0142"
                                value={number} onChange={(e) => setNumber(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOutside(); } }} />
                              <button type="button" className="arc-btn arc-btn-sm" disabled={!typed} onClick={addOutside}>
                                <Icon name="plus" size={12} /> Add number
                              </button>
                            </>
                          )}
                        />
                      )}

                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {steps.length < MAX_STEPS && (
            <div className="arc-flow-link">
              <span className="arc-flow-rail" aria-hidden />
              <div className="arc-flow-body">
                <button type="button" className="arc-btn arc-btn-sm"
                  onClick={() => { setSteps((all) => [...all, { strategy: 'simultaneous', ring: '20', prompt: '', members: [] }]); setActive(steps.length); }}>
                  <Icon name="plus" size={12} /> Add a step
                </button>
              </div>
            </div>
          )}

          {/* Where every call ends that nobody answered. */}
          <div className="arc-flow-end">
            <span className="arc-step-n" aria-hidden><Icon name="chevronDown" size={12} /></span>
            <div className="arc-field" style={{ margin: 0, flex: 1 }}>
              <label className="arc-label">If nobody answers any step, send the caller to</label>
              <DestinationPicker tenantId={tenantId} value={timeout_} onChange={setTimeout_} allowNone />
            </div>
          </div>
        </div>
        {/* For every step's outside numbers, so with the list's own settings rather than in step 1. */}
        {hasOutside && (
          <div className="arc-field" style={{ maxWidth: 420, marginTop: 16, marginBottom: 0 }}>
            <label className="arc-label" htmlFor="rg-outside-seat">Outside numbers on this list are charged to</label>
            <select id="rg-outside-seat" className="arc-select" value={outsideSeat} onChange={(e) => setOutsideSeat(e.target.value)}>
              <option value="">Choose an extension…</option>
              {everyone.map((e) => <option key={e.id} value={e.id}>{e.number} {e.display_name}</option>)}
            </select>
            <div className="arc-hint">
              Calls to outside numbers go out through your outbound routes as this extension: its call
              permissions, spend limit and caller ID apply, and the calls are billed like its own.
            </div>
          </div>
        )}
      </div>

      <div className="arc-row" style={{ gap: 8 }}>
        {!panel && (
          <>
            <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending || !!why}>
              {save.isPending ? 'Saving…' : existing ? 'Save changes' : 'Create hunt list'}
            </button>
            <button type="button" className="arc-btn" onClick={onDone}>Cancel</button>
          </>
        )}
        {why && <span className="arc-muted" style={{ fontSize: 12.5 }}>{why}</span>}
      </div>
      {save.error && <ErrorBanner error={save.error} />}
    </form>
  );
}
